// app/providers.tsx
import { useEffect, useState, type PropsWithChildren } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  useQueryClient,
} from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { router } from "~/router";
import { getTenantSlugFromPath } from "~/utils/tenant";
import { TenantProvider, useTenant } from "~/hooks/useTenant";
import { Spinner } from "~/components/ui/spinner";
import {
  consumePostLogoutRedirect,
  isLogoutNavigationInProgress,
  isRecoverableAuthError,
  recoverFromBrokenAuthSession,
} from "~/lib/auth-recovery";
import {
  setLoginStatusCookie,
  clearLoginStatusCookie,
} from "~/lib/login-status-cookie";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // Check if it's an auth error (401 or 403)
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 403) {
          // Just clear queries - don't redirect
          // Auth0 will update isAuthenticated state
          // Route guards will handle the redirect
          console.warn(`[Auth] Query failed with ${status}, clearing cache`);
          queryClient.clear();
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        if (error && typeof error === "object" && "status" in error) {
          const status = (error as { status: number }).status;
          if (status === 401 || status === 403) return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe - used by payment components via getStripePromise()
const stripePromise = loadStripe(stripeKey ?? "");

export function getStripePromise() {
  return stripePromise;
}

// Get organization and invitation from URL if provided (for org invitations)
// Supports both legacy format (?invitation=...&organization=...) and
// new JWT format (?invite_token=JWT) where the JWT contains both.
function getAuthParamsFromUrl(): { organization?: string; invitation?: string } {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);

  // Check for JWT-encoded invite token first (new format)
  const inviteToken = params.get("invite_token");
  if (inviteToken) {
    try {
      const base64url = inviteToken.split(".")[1];
      if (base64url) {
        const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
        const payload = JSON.parse(atob(padded));
        const organization = typeof payload.organization === "string" ? payload.organization : undefined;
        const invitation = typeof payload.invitation === "string" ? payload.invitation : undefined;
        if (organization && invitation) {
          return { organization, invitation };
        }
      }
    } catch {
      // Fall through to legacy format
    }
  }

  // Legacy format: raw params in URL
  const organization = params.get("organization") || undefined;
  const invitation = params.get("invitation") || undefined;
  return { organization, invitation };
}

function Auth0ProviderWithTenant({ children }: PropsWithChildren) {
  if (!domain || !clientId || !audience) {
    throw new Error("Auth0 env vars are missing");
  }

  const tenant = useTenant();
  const tenantSlug = getTenantSlugFromPath();

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    const returnTo = appState?.returnTo;
    const target = returnTo && returnTo.startsWith("/") ? returnTo : window.location.pathname;
    // Use router.navigate instead of window.location.href to avoid a full
    // page reload. A full reload forces the Auth0 SDK to re-initialize from
    // localStorage and proactively refresh the token, which can fail with 403
    // if refresh token rotation/reuse detection is misconfigured in Auth0.
    // router.navigate keeps the SDK's in-memory state (fresh tokens from the
    // code exchange) intact while also preserving TanStack Router's internal
    // history state (raw replaceState wipes __TSR_STATE, breaking subsequent
    // client-side navigation).
    router.navigate({ to: target, replace: true });
  };

  // Support organization invitations and org-specific login
  const { organization, invitation } = getAuthParamsFromUrl();

  // URL params (invitation links) take priority over path-based tenant detection
  const resolvedOrg = organization ?? tenant.orgId ?? undefined;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin + (tenantSlug ? `/${tenantSlug}/` : "/"),
        audience,
        scope: "openid profile email offline_access",
        // Include organization if resolved from URL params or tenant path
        ...(resolvedOrg && { organization: resolvedOrg }),
        // Include invitation token if present (required for accepting org invitations)
        ...(invitation && { invitation }),
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <LoginStatusCookieSync />
      <AuthSessionRecovery />
      <PostLogoutRedirector />
      {children}
    </Auth0Provider>
  );
}

function LoginStatusCookieSync() {
  const { isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated) {
      setLoginStatusCookie();
    } else {
      clearLoginStatusCookie();
    }
  }, [isAuthenticated, isLoading]);

  return null;
}

function AuthSessionRecovery() {
  const { error, logout } = useAuth0();
  const queryClient = useQueryClient();
  const [isRecovering, setIsRecovering] = useState(false);

  useEffect(() => {
    if (!error || !isRecoverableAuthError(error)) {
      return;
    }

    console.warn(
      "[Auth] Recoverable Auth0 session error detected during SDK bootstrap:",
      error
    );
    setIsRecovering(true);

    void recoverFromBrokenAuthSession({
      logout,
      queryClient,
    });
  }, [error, logout, queryClient]);

  if (!isRecovering) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/90 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3 px-6 text-center">
        <Spinner className="size-8 text-brand-green-primary" />
        <div>
          <p className="text-sm font-medium text-foreground">
            Your session has expired.
          </p>
          <p className="text-sm text-muted-foreground">
            Signing you out and refreshing the login page.
          </p>
        </div>
      </div>
    </div>
  );
}

function PostLogoutRedirector() {
  const { isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }

    // If a logout navigation is already in progress (window.location.replace
    // to Auth0's /v2/logout has been called), do NOT redirect. Our redirect
    // would override the Auth0 logout URL and prevent the session cookie
    // from being cleared.
    if (isLogoutNavigationInProgress()) {
      return;
    }

    const pendingRedirect = consumePostLogoutRedirect();
    const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (!pendingRedirect || pendingRedirect === currentLocation) {
      return;
    }

    // Circuit breaker: prevent rapid redirect loops by checking if we've
    // already redirected recently (within 5 seconds)
    const REDIRECT_COOLDOWN_KEY = "ph_last_post_logout_redirect";
    const lastRedirect = sessionStorage.getItem(REDIRECT_COOLDOWN_KEY);
    const now = Date.now();
    if (lastRedirect && now - parseInt(lastRedirect, 10) < 5000) {
      console.warn("[Auth] Post-logout redirect suppressed — too many redirects in quick succession");
      return;
    }
    sessionStorage.setItem(REDIRECT_COOLDOWN_KEY, now.toString());

    window.location.replace(pendingRedirect);
  }, [isAuthenticated, isLoading]);

  return null;
}

export default function Providers({ children }: PropsWithChildren) {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <Auth0ProviderWithTenant>{children}</Auth0ProviderWithTenant>
      </TenantProvider>
    </QueryClientProvider>
  );
}
